(() => {
  const ROOT_SELECTOR = '[data-permanent-trim-customizer]';
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const STANDARD_PREVIEW_COLOR = '#f2f2ef';

  const COMMON_COLORS = {
    white: '#f2f2ef',
    black: '#232426',
    brown: '#5b3a29',
    bronze: '#6f5133',
    tan: '#b79b77',
    beige: '#d7c7a8',
    almond: '#ded2b5',
    gray: '#747b80',
    grey: '#747b80',
    charcoal: '#3c4145'
  };

  const normalizeHex = (value, fallback = '') => {
    const raw = String(value || '').trim().replace('#', '');
    if (/^[0-9a-f]{3}$/i.test(raw)) return `#${raw.split('').map((character) => character + character).join('')}`.toLowerCase();
    if (/^[0-9a-f]{6}$/i.test(raw)) return `#${raw}`.toLowerCase();
    return fallback;
  };

  const parseColor = (value, fallback = '#f2f2ef') => {
    const hex = normalizeHex(value);
    if (hex) return hex;
    const rgbMatch = String(value || '').match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
    if (rgbMatch) {
      return rgbToHex({ r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) });
    }
    return fallback;
  };

  const rgbToHex = ({ r, g, b }) => `#${[r, g, b]
    .map((channel) => clamp(Math.round(Number(channel) || 0), 0, 255).toString(16).padStart(2, '0'))
    .join('')}`;

  const hexToRgb = (hex) => {
    const normalized = normalizeHex(hex, '#f2f2ef').slice(1);
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16)
    };
  };

  const hsvToRgb = (hue, saturation, value = 1) => {
    const h = ((hue % 360) + 360) % 360;
    const s = clamp(saturation, 0, 1);
    const v = clamp(value, 0, 1);
    const chroma = v * s;
    const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
    const match = v - chroma;
    let red = 0;
    let green = 0;
    let blue = 0;

    if (h < 60) [red, green, blue] = [chroma, x, 0];
    else if (h < 120) [red, green, blue] = [x, chroma, 0];
    else if (h < 180) [red, green, blue] = [0, chroma, x];
    else if (h < 240) [red, green, blue] = [0, x, chroma];
    else if (h < 300) [red, green, blue] = [x, 0, chroma];
    else [red, green, blue] = [chroma, 0, x];

    return {
      r: Math.round((red + match) * 255),
      g: Math.round((green + match) * 255),
      b: Math.round((blue + match) * 255)
    };
  };

  const hexToHsv = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    const red = r / 255;
    const green = g / 255;
    const blue = b / 255;
    const max = Math.max(red, green, blue);
    const min = Math.min(red, green, blue);
    const delta = max - min;
    let hue = 0;

    if (delta !== 0) {
      if (max === red) hue = 60 * (((green - blue) / delta) % 6);
      else if (max === green) hue = 60 * ((blue - red) / delta + 2);
      else hue = 60 * ((red - green) / delta + 4);
    }

    return { h: (hue + 360) % 360, s: max === 0 ? 0 : delta / max, v: max };
  };

  const smoothstep = (edge0, edge1, value) => {
    const normalized = clamp((value - edge0) / (edge1 - edge0), 0, 1);
    return normalized * normalized * (3 - 2 * normalized);
  };

  // Normalized outline of the supplied PRML_EndCap_2.png track. It keeps the
  // live coating away from the photograph's cast shadow and white backdrop.
  const DEFAULT_TRACK_SILHOUETTE = [
    [0, 0], [0.075, 0], [0.985, 0.558], [0.835, 0.615],
    [0.816, 0.92], [0.36, 0.99], [0.31, 0.955], [0.195, 0.705], [-0.01, 0.02]
  ];

  const isInsideDefaultTrack = (x, y) => {
    let inside = false;
    for (let index = 0, previous = DEFAULT_TRACK_SILHOUETTE.length - 1; index < DEFAULT_TRACK_SILHOUETTE.length; previous = index, index += 1) {
      const [currentX, currentY] = DEFAULT_TRACK_SILHOUETTE[index];
      const [previousX, previousY] = DEFAULT_TRACK_SILHOUETTE[previous];
      const crossesRow = (currentY > y) !== (previousY > y);
      if (crossesRow && x < ((previousX - currentX) * (y - currentY)) / (previousY - currentY) + currentX) inside = !inside;
    }
    return inside;
  };

  class PermanentTrimCustomizer {
    constructor(root) {
      if (root.dataset.permanentTrimReady === 'true') return;
      root.dataset.permanentTrimReady = 'true';

      this.root = root;
      this.form = root.closest('form[action*="/cart/add"]');
      this.source = root.querySelector('[data-permanent-track-source]');
      this.canvas = root.querySelector('[data-permanent-track-canvas]');
      this.layeredPreview = root.dataset.layeredPreview === 'true';
      this.layerImages = {
        background: root.querySelector('[data-permanent-layer="background"]'),
        mask: root.querySelector('[data-permanent-layer="mask"]'),
        shading: root.querySelector('[data-permanent-layer="shading"]'),
        highlights: root.querySelector('[data-permanent-layer="highlights"]')
      };
      this.wheel = root.querySelector('[data-permanent-color-wheel]');
      this.marker = root.querySelector('[data-permanent-color-marker]');
      this.controls = root.querySelector('[data-permanent-custom-controls]');
      this.standardNote = root.querySelector('[data-permanent-standard-note]');
      this.standardHeading = root.querySelector('[data-permanent-standard-heading]');
      this.colorName = root.querySelector('[data-permanent-color-name]');
      this.nativeColor = root.querySelector('[data-permanent-native-color]');
      this.hexInput = root.querySelector('[data-permanent-hex]');
      this.rgbInputs = Array.from(root.querySelectorAll('[data-permanent-rgb]'));
      this.propertyHex = root.querySelector('[data-permanent-property-hex]');
      this.propertyRgb = root.querySelector('[data-permanent-property-rgb]');
      this.status = root.querySelector('[data-permanent-color-status]');
      this.trimOptionName = String(root.dataset.trimOptionName || 'Trim color').trim().toLowerCase();
      this.customKeyword = String(root.dataset.customKeyword || 'Custom').trim().toLowerCase();
      this.tintStrength = clamp(Number(root.dataset.tintStrength) || 0.92, 0.5, 1);
      const shadingStrength = Number(root.dataset.shadingStrength);
      const highlightStrength = Number(root.dataset.highlightStrength);
      this.shadingStrength = clamp(Number.isFinite(shadingStrength) ? shadingStrength : 1, 0, 1);
      this.highlightStrength = clamp(Number.isFinite(highlightStrength) ? highlightStrength : 1, 0, 1);
      this.protectCastShadows = root.dataset.protectCastShadows !== 'false';
      this.selectedTrim = this.getSelectedTrim();
      this.customActive = this.isCustom(this.selectedTrim);
      this.storageKey = `hlo-permanent-trim-${root.dataset.productId || 'product'}`;
      this.presets = this.readPresets();
      this.dragging = false;
      this.frame = null;
      this.baseImageData = null;
      this.outputImageData = null;
      this.layeredReady = false;
      this.colorLayerCanvas = null;
      this.renderWidth = 0;
      this.renderHeight = 0;

      const stored = this.customActive ? this.readStoredColor() : '';
      this.color = this.customActive
        ? stored || parseColor(root.dataset.defaultColor)
        : STANDARD_PREVIEW_COLOR;
      this.hsv = hexToHsv(this.color);

      if (!this.selectedTrim) {
        this.root.hidden = true;
        return;
      }

      this.bindEvents();
      this.setMode(this.selectedTrim);
      this.updateUi(false, false);
      this.loadImage();
    }

    isCustom(value) {
      return String(value || '').toLowerCase().includes(this.customKeyword);
    }

    isTrimFieldset(fieldset) {
      if (!fieldset) return false;
      const explicitName = String(fieldset.dataset.optionName || '').trim().toLowerCase();
      if (explicitName) return explicitName === this.trimOptionName;
      const fieldName = String(fieldset.querySelector('input[name], select[name]')?.name || '').trim().toLowerCase();
      const legend = String(fieldset.querySelector('legend')?.textContent || '').trim().toLowerCase();
      return fieldName.includes(this.trimOptionName) || legend.startsWith(this.trimOptionName);
    }

    findTrimFieldset() {
      return Array.from(this.form?.querySelectorAll('fieldset') || []).find((fieldset) => this.isTrimFieldset(fieldset));
    }

    getSelectedTrim() {
      const fieldset = this.findTrimFieldset();
      return fieldset?.querySelector('input[type="radio"]:checked')?.value || fieldset?.querySelector('select')?.value || '';
    }

    readPresets() {
      const presets = new Map();
      this.root.querySelectorAll('[data-permanent-trim-preset]').forEach((node) => {
        const name = String(node.dataset.name || '').trim().toLowerCase();
        const color = parseColor(node.dataset.color, '');
        if (name && color) presets.set(name, color);
      });
      return presets;
    }

    readStoredColor() {
      try { return normalizeHex(sessionStorage.getItem(this.storageKey)); } catch (error) { return ''; }
    }

    storeColor() {
      try { sessionStorage.setItem(this.storageKey, this.color); } catch (error) { /* Storage is optional. */ }
    }

    colorForTrim(name) {
      const normalized = String(name || '').trim().toLowerCase();
      if (this.presets.has(normalized)) return this.presets.get(normalized);
      const matchingCommonName = Object.keys(COMMON_COLORS).find((colorName) => normalized.includes(colorName));
      return matchingCommonName ? COMMON_COLORS[matchingCommonName] : '';
    }

    setMode(trimName) {
      const wasCustom = this.customActive;
      this.selectedTrim = trimName || '';
      this.customActive = this.isCustom(this.selectedTrim);
      if (this.controls) this.controls.hidden = !this.customActive;
      if (this.standardNote) this.standardNote.hidden = this.customActive;
      if (this.standardHeading) this.standardHeading.textContent = `${this.selectedTrim} trim selected`;
      if (this.colorName) this.colorName.textContent = this.customActive ? this.color.toUpperCase() : this.selectedTrim;

      [this.propertyHex, this.propertyRgb].forEach((input) => {
        if (input) input.disabled = !this.customActive;
      });
      if (this.hexInput) this.hexInput.required = this.customActive;

      if (this.customActive && !wasCustom) {
        const storedColor = this.readStoredColor();
        this.color = storedColor || STANDARD_PREVIEW_COLOR;
        this.hsv = hexToHsv(this.color);
      } else if (!this.customActive) {
        this.color = STANDARD_PREVIEW_COLOR;
        this.hsv = hexToHsv(this.color);
      }
      this.updateUi(false, false);
    }

    bindEvents() {
      if (this.form) {
        this.form.addEventListener('change', (event) => {
          const fieldset = event.target.closest('fieldset');
          if (!this.isTrimFieldset(fieldset)) return;
          this.setMode(event.target.value);
        });
      }

      if (this.wheel) {
        this.wheel.addEventListener('pointerdown', (event) => {
          this.dragging = true;
          this.wheel.setPointerCapture(event.pointerId);
          this.setFromPointer(event, false);
        });
        this.wheel.addEventListener('pointermove', (event) => {
          if (this.dragging) this.setFromPointer(event, false);
        });
        const finishPointer = (event) => {
          if (!this.dragging) return;
          this.dragging = false;
          this.setFromPointer(event, true);
        };
        this.wheel.addEventListener('pointerup', finishPointer);
        this.wheel.addEventListener('pointercancel', () => { this.dragging = false; });
        this.wheel.addEventListener('keydown', (event) => this.onWheelKeydown(event));
      }

      if (this.nativeColor) {
        this.nativeColor.addEventListener('input', (event) => this.setColor(event.target.value, false));
        this.nativeColor.addEventListener('change', () => this.announce());
      }

      if (this.hexInput) {
        this.hexInput.addEventListener('input', (event) => {
          const color = normalizeHex(event.target.value);
          event.target.setAttribute('aria-invalid', color ? 'false' : 'true');
          if (color) this.setColor(color, false);
        });
        this.hexInput.addEventListener('change', () => {
          const color = normalizeHex(this.hexInput.value, this.color);
          this.setColor(color, true);
        });
      }

      this.rgbInputs.forEach((input) => {
        input.addEventListener('input', () => {
          if (this.rgbInputs.some((field) => field.value === '')) return;
          const channels = Object.fromEntries(this.rgbInputs.map((field) => [field.dataset.permanentRgb, clamp(Number(field.value), 0, 255)]));
          this.setColor(rgbToHex(channels), false);
        });
        input.addEventListener('change', () => this.announce());
      });
    }

    onWheelKeydown(event) {
      const hueStep = event.shiftKey ? 15 : 3;
      const saturationStep = event.shiftKey ? 0.1 : 0.03;
      let handled = true;
      if (event.key === 'ArrowLeft') this.hsv.h = (this.hsv.h - hueStep + 360) % 360;
      else if (event.key === 'ArrowRight') this.hsv.h = (this.hsv.h + hueStep) % 360;
      else if (event.key === 'ArrowUp') this.hsv.s = clamp(this.hsv.s + saturationStep, 0, 1);
      else if (event.key === 'ArrowDown') this.hsv.s = clamp(this.hsv.s - saturationStep, 0, 1);
      else if (event.key === 'Home') this.hsv.s = 0;
      else if (event.key === 'End') this.hsv.s = 1;
      else handled = false;
      if (!handled) return;
      event.preventDefault();
      this.hsv.v = 1;
      this.color = rgbToHex(hsvToRgb(this.hsv.h, this.hsv.s, this.hsv.v));
      this.updateUi(true, true);
    }

    setFromPointer(event, announce) {
      const bounds = this.wheel.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      let x = (event.clientX - bounds.left - bounds.width / 2) / (bounds.width / 2);
      let y = (event.clientY - bounds.top - bounds.height / 2) / (bounds.height / 2);
      const distance = Math.sqrt(x * x + y * y);
      if (distance > 1) {
        x /= distance;
        y /= distance;
      }
      this.hsv = {
        h: (Math.atan2(y, x) * 180 / Math.PI + 360) % 360,
        s: clamp(distance, 0, 1),
        v: 1
      };
      this.color = rgbToHex(hsvToRgb(this.hsv.h, this.hsv.s, 1));
      this.updateUi(announce, true);
    }

    setColor(color, announce) {
      this.color = normalizeHex(color, this.color);
      this.hsv = hexToHsv(this.color);
      this.updateUi(announce, true);
    }

    updateUi(announce, persist) {
      const { r, g, b } = hexToRgb(this.color);
      const angle = this.hsv.h * Math.PI / 180;
      const markerX = 50 + Math.cos(angle) * this.hsv.s * 50;
      const markerY = 50 + Math.sin(angle) * this.hsv.s * 50;

      if (this.marker) {
        this.marker.style.left = `${markerX}%`;
        this.marker.style.top = `${markerY}%`;
        this.marker.style.backgroundColor = this.color;
      }
      if (this.wheel) {
        this.wheel.setAttribute('aria-valuenow', String(Math.round(this.hsv.h)));
        this.wheel.setAttribute('aria-valuetext', `${this.color.toUpperCase()}, RGB ${r}, ${g}, ${b}`);
      }
      if (this.colorName && this.customActive) this.colorName.textContent = this.color.toUpperCase();
      if (this.canvas) {
        const previewName = this.customActive ? this.color.toUpperCase() : this.selectedTrim;
        this.canvas.setAttribute('aria-label', `Permanent-lighting track preview in ${previewName}`);
      }
      if (this.nativeColor) this.nativeColor.value = this.color;
      if (this.hexInput && document.activeElement !== this.hexInput) {
        this.hexInput.value = this.color.toUpperCase();
        this.hexInput.setAttribute('aria-invalid', 'false');
      }
      this.rgbInputs.forEach((input) => {
        if (document.activeElement !== input) input.value = String({ r, g, b }[input.dataset.permanentRgb]);
      });
      if (this.propertyHex) this.propertyHex.value = this.customActive ? this.color.toUpperCase() : '';
      if (this.propertyRgb) this.propertyRgb.value = this.customActive ? `${r}, ${g}, ${b}` : '';
      if (persist && this.customActive) this.storeColor();

      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.drawPreview();
        this.frame = null;
      });
      if (announce) this.announce();
    }

    announce() {
      if (!this.status) return;
      const { r, g, b } = hexToRgb(this.color);
      this.status.textContent = `Trim preview changed to ${this.color.toUpperCase()}, RGB ${r}, ${g}, ${b}.`;
    }

    loadImage() {
      const layers = Object.values(this.layerImages).filter(Boolean);
      if (this.layeredPreview && layers.length === 4) {
        this.loadLayeredImages(layers);
        return;
      }
      if (!this.source || !this.canvas) return;
      const ready = () => {
        try {
          this.prepareImage();
          this.drawPreview();
          this.source.hidden = true;
          this.canvas.hidden = false;
        } catch (error) {
          this.source.hidden = false;
          this.canvas.hidden = true;
        }
      };
      if (this.source.complete && this.source.naturalWidth) ready();
      else {
        this.source.addEventListener('load', ready, { once: true });
        this.source.addEventListener('error', () => {
          this.source.hidden = false;
          this.canvas.hidden = true;
        }, { once: true });
      }
    }

    loadLayeredImages(layers) {
      if (!this.canvas) return;
      let failed = false;
      const fallback = () => {
        if (failed) return;
        failed = true;
        this.layeredPreview = false;
        this.loadImage();
      };
      const ready = () => {
        if (failed || !layers.every((image) => image.complete && image.naturalWidth)) return;
        try {
          this.prepareLayeredImages();
          this.drawPreview();
          if (this.source) this.source.hidden = true;
          this.canvas.hidden = false;
        } catch (_error) {
          fallback();
        }
      };

      layers.forEach((image) => {
        if (!image.complete) image.addEventListener('load', ready, { once: true });
        image.addEventListener('error', fallback, { once: true });
      });
      ready();
    }

    prepareLayeredImages() {
      const { background, mask, shading, highlights } = this.layerImages;
      if (![background, mask, shading, highlights].every((image) => image?.naturalWidth && image?.naturalHeight)) {
        throw new Error('Permanent track layers are incomplete.');
      }
      const width = background.naturalWidth;
      const height = background.naturalHeight;
      if (![mask, shading, highlights].every((image) => image.naturalWidth === width && image.naturalHeight === height)) {
        throw new Error('Permanent track layers must use identical dimensions.');
      }

      const scale = Math.min(1, 760 / width);
      this.renderWidth = Math.round(width * scale);
      this.renderHeight = Math.round(height * scale);
      this.canvas.width = this.renderWidth;
      this.canvas.height = this.renderHeight;
      this.colorLayerCanvas = document.createElement('canvas');
      this.colorLayerCanvas.width = this.renderWidth;
      this.colorLayerCanvas.height = this.renderHeight;
      this.layeredReady = true;
    }

    prepareImage() {
      const context = this.canvas.getContext('2d', { willReadFrequently: true });
      if (!context || !this.source.naturalWidth || !this.source.naturalHeight) return;
      const scale = Math.min(1, 760 / this.source.naturalWidth);
      this.renderWidth = Math.round(this.source.naturalWidth * scale);
      this.renderHeight = Math.round(this.source.naturalHeight * scale);
      this.canvas.width = this.renderWidth;
      this.canvas.height = this.renderHeight;
      context.clearRect(0, 0, this.renderWidth, this.renderHeight);
      context.drawImage(this.source, 0, 0, this.renderWidth, this.renderHeight);
      this.baseImageData = context.getImageData(0, 0, this.renderWidth, this.renderHeight);
      this.outputImageData = context.createImageData(this.renderWidth, this.renderHeight);
    }

    drawPreview() {
      if (this.layeredReady) {
        this.drawLayeredPreview();
        return;
      }
      if (!this.canvas || !this.baseImageData || !this.outputImageData) return;
      const context = this.canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;

      const source = this.baseImageData.data;
      const output = this.outputImageData.data;
      const selected = hexToRgb(this.color);
      const selectedChannels = [selected.r, selected.g, selected.b];

      // Move the chosen coating color toward white when the strength is
      // reduced, then multiply it into the source pixels. This behaves like a
      // Photoshop-style Multiply layer instead of painting an opaque color
      // over the photograph.
      const multiplyChannels = selectedChannels.map((channel) => (
        255 - ((255 - channel) * this.tintStrength)
      ));

      for (let index = 0; index < source.length; index += 4) {
        const pixel = index / 4;
        const normalizedX = (pixel % this.renderWidth) / Math.max(1, this.renderWidth - 1);
        const normalizedY = Math.floor(pixel / this.renderWidth) / Math.max(1, this.renderHeight - 1);
        const originalRed = source[index];
        const originalGreen = source[index + 1];
        const originalBlue = source[index + 2];
        const alpha = source[index + 3];
        const luminance = (0.2126 * originalRed + 0.7152 * originalGreen + 0.0722 * originalBlue) / 255;
        const darkness = 1 - luminance;

        // The source is a white track on a nearly white background. The first
        // mask protects that background; the second protects deep shadows and
        // black details so they stay neutral instead of becoming color-filled.
        // Bright specular highlights also remain mostly white.
        const materialMask = smoothstep(0.006, 0.055, darkness);
        const shadowMask = this.protectCastShadows
          ? smoothstep(0.58, 0.9, luminance)
          : smoothstep(0.78, 0.94, luminance);
        const highlightProtection = 1 - (0.72 * smoothstep(0.93, 0.997, luminance));
        const silhouetteMask = !this.protectCastShadows || isInsideDefaultTrack(normalizedX, normalizedY) ? 1 : 0;
        const colorMask = silhouetteMask * materialMask * shadowMask * highlightProtection;

        for (let channel = 0; channel < 3; channel += 1) {
          const original = source[index + channel];
          const multiplied = (original * multiplyChannels[channel]) / 255;
          output[index + channel] = Math.round(original * (1 - colorMask) + multiplied * colorMask);
        }
        output[index + 3] = alpha;
      }

      context.putImageData(this.outputImageData, 0, 0);
    }

    drawLayeredPreview() {
      if (!this.canvas || !this.colorLayerCanvas) return;
      const context = this.canvas.getContext('2d');
      const colorContext = this.colorLayerCanvas.getContext('2d');
      if (!context || !colorContext) return;

      const { background, mask, shading, highlights } = this.layerImages;
      const selected = hexToRgb(this.color);
      const coating = {
        r: Math.round(255 - ((255 - selected.r) * this.tintStrength)),
        g: Math.round(255 - ((255 - selected.g) * this.tintStrength)),
        b: Math.round(255 - ((255 - selected.b) * this.tintStrength))
      };

      colorContext.clearRect(0, 0, this.renderWidth, this.renderHeight);
      colorContext.globalCompositeOperation = 'source-over';
      colorContext.fillStyle = `rgb(${coating.r} ${coating.g} ${coating.b})`;
      colorContext.fillRect(0, 0, this.renderWidth, this.renderHeight);
      colorContext.globalCompositeOperation = 'destination-in';
      colorContext.drawImage(mask, 0, 0, this.renderWidth, this.renderHeight);
      colorContext.globalCompositeOperation = 'source-over';

      context.clearRect(0, 0, this.renderWidth, this.renderHeight);
      context.globalCompositeOperation = 'source-over';
      context.globalAlpha = 1;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, this.renderWidth, this.renderHeight);
      context.drawImage(background, 0, 0, this.renderWidth, this.renderHeight);
      context.drawImage(this.colorLayerCanvas, 0, 0);

      context.globalCompositeOperation = 'multiply';
      context.globalAlpha = this.shadingStrength;
      context.drawImage(shading, 0, 0, this.renderWidth, this.renderHeight);

      context.globalCompositeOperation = 'screen';
      context.globalAlpha = this.highlightStrength;
      context.drawImage(highlights, 0, 0, this.renderWidth, this.renderHeight);

      context.globalCompositeOperation = 'source-over';
      context.globalAlpha = 1;
    }
  }

  class PermanentProductEnhancements {
    constructor(root) {
      if (root.dataset.permanentEnhancementsReady === 'true') return root.permanentProductEnhancements;
      root.dataset.permanentEnhancementsReady = 'true';
      this.root = root;
      this.shippingTemplate = root.querySelector('[data-permanent-shipping-template]');
      this.lifestyleTemplate = root.querySelector('[data-permanent-lifestyle-template]');
      this.appTemplate = root.querySelector('[data-permanent-app-template]');
      this.trimTemplate = root.querySelector('[data-permanent-trim-template]');
      this.variantMediaMap = [...root.querySelectorAll('[data-permanent-variant-media]')].map((node) => {
        let options = [];
        try { options = JSON.parse(node.dataset.options || '[]'); } catch (_error) { options = []; }
        return {
          mediaId: String(node.dataset.mediaId || ''),
          variantId: String(node.dataset.variantId || ''),
          available: node.dataset.available === 'true',
          options
        };
      }).filter((entry) => entry.mediaId && entry.variantId && entry.options.length);
      root.permanentProductEnhancements = this;
      this.injectScope(document);
    }

    injectScope(scope) {
      const sections = [];
      if (scope.matches?.('[data-product-section]')) sections.push(scope);
      scope.querySelectorAll?.('[data-product-section]').forEach((section) => sections.push(section));
      sections.forEach((section) => this.injectProduct(section));
    }

    injectProduct(productSection) {
      const info = productSection.querySelector('.product-info-v2');
      const form = productSection.querySelector('form[action*="/cart/add"]');
      if (!info || !form) return;

      // Use the complete variant-to-media map below. The theme's generic
      // gallery matcher only sees one representative variant per option value,
      // which can be ambiguous on products with several option groups.
      const gallery = productSection.querySelector('[data-product-gallery]');
      if (gallery) gallery.dataset.syncVariant = 'false';

      if (this.lifestyleTemplate && gallery && !gallery.querySelector('[data-permanent-injected-lifestyle]')) {
        const lifestyleGallery = this.lifestyleTemplate.content.firstElementChild?.cloneNode(true);
        if (lifestyleGallery) {
          lifestyleGallery.dataset.permanentInjectedLifestyle = 'true';
          gallery.append(lifestyleGallery);
        }
      }

      if (this.appTemplate && !productSection.querySelector('[data-permanent-injected-app]')) {
        const appCallout = this.appTemplate.content.firstElementChild?.cloneNode(true);
        if (appCallout) {
          appCallout.dataset.permanentInjectedApp = 'true';
          const stockStatus = info.querySelector('.stock-status');
          if (stockStatus) stockStatus.after(appCallout);
          else info.prepend(appCallout);
        }
      }

      if (this.trimTemplate && !productSection.querySelector('[data-permanent-trim-customizer]')) {
        const customizer = this.trimTemplate.content.firstElementChild?.cloneNode(true);
        const picker = form.querySelector('[data-option-picker], .option-picker');
        if (customizer && picker) {
          picker.after(customizer);
          new PermanentTrimCustomizer(customizer);
        }
      }

      if (this.shippingTemplate && !info.querySelector('[data-permanent-injected-shipping]')) {
        const shippingNotice = this.shippingTemplate.content.firstElementChild?.cloneNode(true);
        if (shippingNotice) {
          shippingNotice.dataset.permanentInjectedShipping = 'true';
          const pickup = info.querySelector('.pickup-card');
          const utilityLinks = info.querySelector('.product-utility-links');
          if (pickup) pickup.before(shippingNotice);
          else if (utilityLinks) utilityLinks.before(shippingNotice);
          else form.after(shippingNotice);
        }
      }
    }

    syncVariantToActiveMedia(gallery) {
      if (!gallery || !this.variantMediaMap.length) return;
      const productSection = gallery.closest('[data-product-section]');
      const picker = productSection?.querySelector('[data-option-picker]');
      if (!productSection || !picker) return;

      const mediaId = String(gallery.dataset.activeMediaId || '');
      const candidates = this.variantMediaMap.filter((entry) => entry.mediaId === mediaId);
      if (!candidates.length) return;

      const fieldsets = [...picker.querySelectorAll(':scope > fieldset')];
      const selectedOptions = fieldsets.map((fieldset) => {
        const radio = fieldset.querySelector('input[data-option-value-id]:checked');
        const select = fieldset.querySelector('select[data-option-select]');
        return radio?.value ?? select?.value ?? '';
      });

      const ranked = candidates.map((entry) => ({
        entry,
        score: entry.options.reduce((total, value, index) => (
          total + (String(value) === String(selectedOptions[index] || '') ? 1 : 0)
        ), 0)
      })).sort((a, b) => (b.score - a.score) || (Number(b.entry.available) - Number(a.entry.available)));
      const target = ranked[0]?.entry;
      if (!target) return;

      const currentVariantId = productSection.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
      if (String(currentVariantId || '') === target.variantId) return;

      const changedControls = [];
      fieldsets.forEach((fieldset, index) => {
        const value = String(target.options[index] ?? '');
        if (!value) return;
        const radios = [...fieldset.querySelectorAll('input[data-option-value-id]')];
        if (radios.length) {
          const radio = radios.find((control) => control.value === value);
          if (radio && !radio.checked) {
            radio.checked = true;
            changedControls.push(radio);
          }
          return;
        }
        const select = fieldset.querySelector('select[data-option-select]');
        if (select && select.value !== value && [...select.options].some((option) => option.value === value)) {
          select.value = value;
          changedControls.push(select);
        }
      });

      const trigger = changedControls[changedControls.length - 1];
      if (!trigger) return;
      trigger.dataset.gallerySync = 'true';
      trigger.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  const initialize = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => new PermanentTrimCustomizer(root));
  };

  const initializeEnhancements = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-permanent-enhancements]')) roots.push(scope);
    scope.querySelectorAll?.('[data-permanent-enhancements]').forEach((root) => roots.push(root));
    roots.forEach((root) => new PermanentProductEnhancements(root));
  };

  const reinjectEnhancements = (scope) => {
    document.querySelectorAll('[data-permanent-enhancements]').forEach((root) => {
      const instance = root.permanentProductEnhancements || new PermanentProductEnhancements(root);
      instance?.injectScope(scope);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeEnhancements();
      initialize();
    });
  } else {
    initializeEnhancements();
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeEnhancements(event.target);
    reinjectEnhancements(event.target);
    initialize(event.target);
  });

  document.addEventListener('click', (event) => {
    const galleryControl = event.target.closest('[data-media-target], [data-gallery-step]');
    if (!galleryControl || !event.isTrusted) return;
    const gallery = galleryControl.closest('[data-product-gallery]');
    if (!gallery) return;

    // The theme gallery updates its active media during the same click event.
    // Read that final media id on the next frame, then select its exact variant.
    requestAnimationFrame(() => {
      const root = document.querySelector('[data-permanent-enhancements]');
      const instance = root?.permanentProductEnhancements;
      instance?.syncVariantToActiveMedia(gallery);
    });
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      initializeEnhancements(node);
      reinjectEnhancements(node);
      initialize(node);
    }));
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
